/**
 * **Lazy Rendering der Zeilen** (#303, Runde 2) — der reine Teil: welche Zeile ihren Inhalt bauen darf.
 *
 * Jede Zeile behält ihre Hülle (Drag-Ziel, `data-task-id`); nur `TaskEntry` darin wird erst gebaut, wenn die
 * Zeile in die Nähe des sichtbaren Bereichs kommt, und weit draußen durch einen Placeholder mit gemerkter Höhe
 * ersetzt. Gemessen 10.10.2026: Das Bauen aller Zeilen auf einmal ist fast die ganze Zeit beim Öffnen eines
 * Projekts (196 Zeilen, ~1,1 s JavaScript, davon ~20 sichtbar).
 */

/**
 * So viele Zeilen baut eine Seite beim ersten Bild sofort — in Dokument-Reihenfolge, ohne auf den Observer zu warten.
 * Etwas mehr als ein Fenster voll zugeklappter Zeilen (860 px / 38 px ≈ 22): Jede gebaute Zeile kostet beim Öffnen und
 * bei jeder Auswahl, die sie trifft (⌘A), gemessen 10.10.2026.
 */
export const INITIAL_BUDGET = 22
/** Ab diesem Abstand zum sichtbaren Bereich wird gebaut (Anteil der Höhe des Scrollcontainers) — genug für zügiges Scrollen. */
export const BUILD_MARGIN = '40%'
/**
 * Ab diesem Abstand wird wieder entladen — weiter draußen als gebaut wird, damit nichts flackert. Nicht zu weit: Was
 * gebaut bleibt, rendert bei jeder Auswahl mit, die es trifft (gemessen 10.10.2026: mit 300 % blieben nach einem Scroll
 * bis Zeile 100 und zurück ~90 Zeilen gebaut, ⌘A dauerte 115 ms; mit 150 % noch ~60 und gut 100 ms; 60 % hält nahe am Vorbau).
 */
export const UNLOAD_MARGIN = '60%'
/** Höhe einer nie gebauten Zeile, bis gemessene da sind: eine zugeklappte, einzeilige Zeile samt Abstand. */
export const ESTIMATED_ROW_HEIGHT = 38

/**
 * Ereignis, das eine Zeile beim Wechsel zwischen Hülle und Inhalt nach oben schickt. Die Auswahl-Fläche
 * (`HullLayer`) misst daraufhin nach: Eine nachgebaute, ausgewählte Zeile (⌘A, dann scrollen) stünde sonst ohne
 * Markierung da — sie malt sie nicht selbst, solange die Fläche zuständig ist.
 */
export const LAZY_ROW_CHANGE = 'lazy-row-change'

export type RowZone = 'near' | 'far'

/** Ein Observer je Rand — eingespeist, damit Tests ihn selbst auslösen können. */
export type ObserverFactory = (
  root: Element | null,
  rootMargin: string,
  onEntries: (entries: ReadonlyArray<Pick<IntersectionObserverEntry, 'target' | 'isIntersecting' | 'boundingClientRect'>>) => void,
) => Pick<IntersectionObserver, 'observe' | 'unobserve' | 'disconnect'>

export interface LazyRows {
  /** Beim ersten Rendern einer Zeile: darf sie sofort bauen? Gilt je Kennung nur einmal (StrictMode rendert doppelt). */
  claim(id: string): boolean
  /** Die gemerkte Höhe, sonst der Durchschnitt der gemessenen, sonst die Schätzung. */
  height(id: string): number
  remember(id: string, height: number): void
  /** Der Scroller über der Zeile — einmal je Seite gesucht, dann gemerkt. */
  root(el: Element): Element | null
  /** Meldet, wenn die Zeile in die Nähe kommt (`near`) oder weit hinaus geht (`far`). */
  observe(el: Element, id: string, onZone: (zone: RowZone) => void): () => void
  /** Ein verborgener Tab (TabStack, `display: none`) meldet alles als „weit draußen“ — dann gilt nichts. */
  setActive(active: boolean): void
  disconnect(): void
}

const defaultObserver: ObserverFactory = (root, rootMargin, onEntries) =>
  new IntersectionObserver(entries => onEntries(entries), { root, rootMargin: `${rootMargin} 0px` })

/**
 * Der nächste Vorfahr, der wirklich scrollt. Nicht `getScrollContainer`: Der kennt nicht jeden Scroller (Demnächst
 * scrollt in `.upcoming-page__scroll`), und ein Observer an einem äußeren Element sähe den Vorbau-Abstand nicht —
 * der innere Scroller schneidet alles ab, was nicht im Bild ist.
 */
export function findScrollRoot(el: Element): Element | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return node
  }
  return null
}

export function createLazyRows({ budget = INITIAL_BUDGET, makeObserver = defaultObserver }: { budget?: number; makeObserver?: ObserverFactory } = {}): LazyRows {
  const claimed = new Set<string>()
  const heights = new Map<string, number>()
  let heightSum = 0
  const rows = new Map<Element, { id: string; onZone: (zone: RowZone) => void }>()
  let active = true
  let observers: { near: ReturnType<ObserverFactory>; far: ReturnType<ObserverFactory> } | null = null
  let scrollRoot: Element | null | undefined

  const remember = (id: string, height: number) => {
    // Eine zuklappende oder verborgene Zeile misst 0 — das ist nicht ihre Höhe.
    if (!(height > 0)) return
    heightSum += height - (heights.get(id) ?? 0)
    heights.set(id, height)
  }

  const root = (el: Element) => {
    if (scrollRoot === undefined || (scrollRoot && !scrollRoot.isConnected)) scrollRoot = findScrollRoot(el)
    return scrollRoot
  }

  const ensureObservers = (scroller: Element | null) => {
    if (observers) return observers
    const route = (zone: RowZone) => (entries: Parameters<Parameters<ObserverFactory>[2]>[0]) => {
      if (!active) return
      for (const entry of entries) {
        const row = rows.get(entry.target)
        if (!row) continue
        if (zone === 'near' && entry.isIntersecting) row.onZone('near')
        if (zone === 'far' && !entry.isIntersecting) {
          remember(row.id, entry.boundingClientRect.height)
          row.onZone('far')
        }
      }
    }
    observers = { near: makeObserver(scroller, BUILD_MARGIN, route('near')), far: makeObserver(scroller, UNLOAD_MARGIN, route('far')) }
    return observers
  }

  return {
    claim(id) {
      if (claimed.has(id)) return true
      if (claimed.size >= budget) return false
      claimed.add(id)
      return true
    },
    height: id => heights.get(id) ?? (heights.size > 0 ? Math.round(heightSum / heights.size) : ESTIMATED_ROW_HEIGHT),
    remember,
    root,
    observe(el, id, onZone) {
      const { near, far } = ensureObservers(root(el))
      rows.set(el, { id, onZone })
      near.observe(el)
      far.observe(el)
      return () => {
        rows.delete(el)
        near.unobserve(el)
        far.unobserve(el)
      }
    },
    setActive(next) { active = next },
    disconnect() {
      observers?.near.disconnect()
      observers?.far.disconnect()
      observers = null
      rows.clear()
    },
  }
}
